import { InformationCircle } from 'react-ionicons';
import React, { Fragment, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { usePopper } from 'react-popper';
import clsx from 'clsx';
import { formatBandwidth, formatTerm } from 'screens/SelectServices/utils';
import { formatCurrency } from 'shared/util/formatCurrency';
import { FormalProductOfferings } from 'screens/OpenQuote/QuoteDetails/schema';
import { getLabelForIpBlock } from 'screens/OpenQuote/utils';

export interface ProductTooltipProps {
  product: FormalProductOfferings;
  inReviewProducts?: boolean;
}

const BroadbandProductDescription = ({
  product,
}: {
  product: FormalProductOfferings;
}) => (
  <Fragment>
    <li>Service: Broadband</li>
    <li>
      Bandwidth:{' '}
      {formatBandwidth(
        product.formal_broadband_product?.bandwidth_download ?? '0',
      )}
      /
      {formatBandwidth(
        product.formal_broadband_product?.bandwidth_upload ?? '0',
      )}
    </li>
    <li>IP Type: {product.formal_broadband_product?.ip_type}</li>
    {product.ip_block && (
      <li>
        IP Block: {product.ip_block} - {getLabelForIpBlock(product.ip_block)}{' '}
        usable
      </li>
    )}
    <li>Term length: {formatTerm(product.term)}</li>
    <li>
      Total MRC:{' '}
      {formatCurrency(
        product.formal_broadband_product?.formal_broadband_pricing.total_mrc,
      )}
    </li>
  </Fragment>
);

const DIAProductDescription = ({
  product,
}: {
  product: FormalProductOfferings;
}) => (
  <Fragment>
    <li>Service: DIA</li>
    <li>
      Bandwidth: {formatBandwidth(product.formal_dia_product?.bandwidth ?? '0')}
      /{formatBandwidth(product.formal_dia_product?.bandwidth ?? '0')}
    </li>
    <li>Access type: {product.formal_dia_product?.access_type}</li>
    <li>
      Building status:{' '}
      {product.formal_dia_product?.on_net ? 'On-net' : 'Off-net'}
    </li>
    <li>IP Type: Static</li>
    {product.ip_block && (
      <li>
        IP Block: {product.ip_block} - {getLabelForIpBlock(product.ip_block)}{' '}
        usable
      </li>
    )}
    <li>Term length: {formatTerm(product.term)}</li>
    <li>
      Total MRC:{' '}
      {formatCurrency(product.formal_dia_product?.formal_dia_pricing.total_mrc)}
    </li>
  </Fragment>
);

export const AccessProductTooltip = observer(
  ({ product }: ProductTooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const triggerEltRef = useRef<HTMLDivElement>(null);
    const popperEltRef = useRef<HTMLDivElement>(null);

    const { styles, attributes } = usePopper(
      triggerEltRef?.current,
      popperEltRef?.current,
      {
        placement: 'top-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [-16, 14],
            },
          },
        ],
      },
    );

    const isBroadband = product.product_type === 'Broadband';
    const isDIA = product.product_type === 'DIA';

    const downloadBandwidth =
      product.formal_broadband_product?.bandwidth_download ??
      product.formal_dia_product?.bandwidth ??
      '0';

    const uploadBandwidth =
      product.formal_broadband_product?.bandwidth_upload ??
      product.formal_dia_product?.bandwidth ??
      '0';

    return (
      <Fragment>
        <div
          ref={triggerEltRef}
          className={clsx(
            'flex items-center justify-center gap-1 rounded-lg bg-green-300 px-3 py-2 text-xs font-bold text-content-flip-default hover:bg-green-200',
            !isBroadband &&
              'border border-[#6C9DFF] bg-transparent !text-content-base-default hover:bg-[#6C9DFF] hover:!text-content-flip-default [&>span>svg]:fill-[#6C9DFF] [&>span>svg]:hover:fill-[#191925]',
            isBroadband &&
              'border !border-green-300 bg-transparent !text-content-base-default hover:!bg-green-300 hover:!text-content-flip-default [&>span>svg]:!fill-[#A7FFB2] [&>span>svg]:hover:!fill-[#191925]',
          )}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          <span>{`${product.provider} ${formatBandwidth(
            downloadBandwidth,
          )} / ${formatBandwidth(uploadBandwidth)}`}</span>

          <InformationCircle width="16px" height="16px" color="inherit" />
        </div>
        <div ref={popperEltRef} style={styles.popper} {...attributes.popper}>
          {isVisible && (
            <div className="rounded-lg bg-background-base-surface-0 p-5 text-content-base-subdued shadow-elevation3">
              <ul className="list-inside list-disc text-sm text-content-base-subdued">
                {isBroadband && product.formal_broadband_product ? (
                  <BroadbandProductDescription product={product} />
                ) : isDIA && product.formal_dia_product ? (
                  <DIAProductDescription product={product} />
                ) : (
                  <li>No product data available</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </Fragment>
    );
  },
);
